import React from 'react';
import { 
  Box, 
  Button, 
  Stack, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Container,
  Paper,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import { Link } from 'react-router-dom';
import {
  SwapHoriz as TransferIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Message as MessageIcon,
  Search as SearchIcon,
  CheckCircle as CheckIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';
import TransferHubPromotions from '../components/TransferHubPromotions';

const TransferHub: React.FC = () => {
  return (
    <Container maxWidth="lg">
      <Box sx={{ textAlign: 'center', mb: 6 }}>
        <Typography variant="h2" component="h1" gutterBottom sx={{ fontWeight: 'bold', color: 'primary.main' }}>
          <TransferIcon sx={{ fontSize: '3rem', mr: 2, verticalAlign: 'middle' }} />
          TransferHub
        </Typography>
        <Typography variant="h5" color="text.secondary" sx={{ mb: 4, maxWidth: '800px', mx: 'auto' }}>
          Connect with other government employees for transfers closer to home. 
          Find your perfect match and make your career move seamless.
        </Typography>
      </Box>

      {/* Features Section */}
      <Grid container spacing={4} sx={{ mb: 6 }}>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', textAlign: 'center' }}>
            <CardContent>
              <PeopleIcon sx={{ fontSize: '3rem', color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Browse Profiles</Typography>
              <Typography variant="body2" color="text.secondary">
                Discover verified government employees looking for transfer opportunities across different departments and locations.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', textAlign: 'center' }}>
            <CardContent>
              <WorkIcon sx={{ fontSize: '3rem', color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Job Opportunities</Typography>
              <Typography variant="body2" color="text.secondary">
                Find and post job opportunities within government departments. Connect with employers and candidates.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%', textAlign: 'center' }}>
            <CardContent>
              <MessageIcon sx={{ fontSize: '3rem', color: 'primary.main', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Direct Messaging</Typography>
              <Typography variant="body2" color="text.secondary">
                Communicate directly with potential transfer partners and employers through our secure messaging system.
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Promotions Section */}
      <TransferHubPromotions />

      {/* How It Works */}
      <Paper sx={{ p: 4, mb: 6 }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
          How TransferHub Works
        </Typography>
        <Grid container spacing={4}>
          <Grid item xs={12} md={6}>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Create Your Profile" 
                  secondary="Complete your professional profile with your current position, preferred locations, and qualifications."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Post Transfer Requests" 
                  secondary="Let others know you're looking for transfer opportunities in specific locations or departments."
                />
              </ListItem>
            </List>
          </Grid>
          <Grid item xs={12} md={6}>
            <List>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Find Matches" 
                  secondary="Our system automatically matches you with compatible transfer opportunities and candidates."
                />
              </ListItem>
              <ListItem>
                <ListItemIcon>
                  <CheckIcon color="primary" />
                </ListItemIcon>
                <ListItemText 
                  primary="Connect & Transfer" 
                  secondary="Communicate with matches, coordinate transfers, and make your career move happen."
                />
              </ListItem>
            </List>
          </Grid>
        </Grid>
      </Paper>

      {/* Benefits */}
      <Paper sx={{ p: 4, mb: 6, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
        <Typography variant="h4" gutterBottom sx={{ textAlign: 'center', mb: 4 }}>
          Why Choose TransferHub?
        </Typography>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <SecurityIcon sx={{ fontSize: '2.5rem', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Verified Profiles</Typography>
              <Typography variant="body2">
                All profiles are verified to ensure authenticity and trust in the transfer process.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <SpeedIcon sx={{ fontSize: '2.5rem', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Fast Matching</Typography>
              <Typography variant="body2">
                Our intelligent matching system quickly connects you with relevant opportunities.
              </Typography>
            </Box>
          </Grid>
          <Grid item xs={12} md={4}>
            <Box sx={{ textAlign: 'center' }}>
              <PeopleIcon sx={{ fontSize: '2.5rem', mb: 2 }} />
              <Typography variant="h6" gutterBottom>Government Focused</Typography>
              <Typography variant="body2">
                Designed specifically for government employees and departments across South Africa.
              </Typography>
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* Quick Actions */}
      <Box sx={{ textAlign: 'center' }}>
        <Typography variant="h5" gutterBottom sx={{ mb: 3 }}>
          Get Started Today
        </Typography>
        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap">
          <Button 
            variant="contained" 
            size="large"
            component={Link} 
            to="/transferhub/profile"
            startIcon={<PeopleIcon />}
          >
            Complete Your Profile
          </Button>
          <Button 
            variant="outlined" 
            size="large"
            component={Link} 
            to="/transferhub/profiles"
            startIcon={<SearchIcon />}
          >
            Browse Profiles
          </Button>
          <Button 
            variant="outlined" 
            size="large"
            component={Link} 
            to="/transferhub/requests"
            startIcon={<TransferIcon />}
          >
            Create Transfer Request
          </Button>
        </Stack>
      </Box>
    </Container>
  );
};

export default TransferHub;